import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Keeps each history entry's scroll position and puts it back on a back or
 * forward move (JAR-1480).
 *
 * The app had no scroll handling at all. A back swipe off the map landed at the
 * top of Trip Plan rather than the 580px the user left it at, and a push to a
 * long screen opened at the previous screen's offset.
 *
 * The browser's own restoration cannot do this here. On a pop it restores
 * before the screen has re-rendered — the Dashboard remounts, restores its tab
 * in an effect, and only then renders the list — so the browser clamps to the
 * short page it finds and never tries again. So this takes it over
 * (`history.scrollRestoration = 'manual'`) and retries until the page is tall
 * enough to hold the position.
 *
 * This is `<ScrollRestoration />`'s job, but that component is data-router only
 * and the app mounts a plain `BrowserRouter` (main.tsx).
 */

// Keyed by React Router's per-entry key, which lives in `window.history.state`
// and so survives a reload — that is what keeps a refresh landing where the
// browser used to leave it, now that the browser is no longer doing it.
const STORE_KEY = 'jarvis_scroll_positions';

// How long to keep retrying while the screen's content arrives; a lazily-loaded
// route plus a fetch is the slow case. Past this the position is unreachable —
// the page genuinely is shorter now — and retrying only fights a reader.
const RESTORE_BUDGET_MS = 1500;

type Positions = Record<string, number>;

function readPositions(): Positions {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Positions;
  } catch {
    return {};
  }
}

function writePositions(positions: Positions): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(positions));
  } catch {
    /* private mode, or the quota — scrolling still works, it just won't come back */
  }
}

export function useScrollRestoration(): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  // Which entry a scroll belongs to. Read at the moment the event fires, never
  // when the throttled write runs: a navigation in between would file the
  // outgoing screen's position under the incoming entry.
  const currentKey = useRef(location.key);
  // The URL we came from, so an overlay's same-URL history entry (JAR-1412) can
  // be told from a move to a new screen.
  const previousUrl = useRef<string | null>(null);

  useEffect(() => {
    currentKey.current = location.key;
  }, [location.key]);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    let pending: { key: string; y: number } | null = null;
    let frame = 0;
    const flush = () => {
      frame = 0;
      if (!pending) return;
      const positions = readPositions();
      positions[pending.key] = pending.y;
      writePositions(positions);
      pending = null;
    };
    const onScroll = () => {
      // Both captured now. By the time the frame runs the screen may have
      // changed, and then neither value would be about this entry any more.
      pending = { key: currentKey.current, y: window.scrollY };
      if (!frame) frame = requestAnimationFrame(flush);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
      if (frame) cancelAnimationFrame(frame);
      flush();
    };
  }, []);

  useEffect(() => {
    const url = `${location.pathname}${location.search}`;
    const cameFrom = previousUrl.current;
    previousUrl.current = url;

    // A load or a reload also reports POP, which is what we want: the browser is
    // no longer restoring those, so this has to.
    if (navigationType !== 'POP') {
      // A push that stays on the same URL is an overlay taking a history entry
      // of its own, not a new screen. Opening and closing one leaves the page
      // exactly where it was.
      if (cameFrom === url) return;
      window.scrollTo(0, 0);
      return;
    }

    const target = readPositions()[location.key];
    if (typeof target !== 'number' || target <= 0) {
      // Nothing recorded — a fresh deep link, or a cleared session. The top is
      // the only honest answer, and where the browser would have left it.
      window.scrollTo(0, 0);
      return;
    }

    // Retry until the page is tall enough to hold the position: the screen's
    // content arrives in effects, after this one runs. Stop the moment the user
    // scrolls — from then the position is theirs, not ours.
    let frame = 0;
    let stopped = false;
    const deadline = Date.now() + RESTORE_BUDGET_MS;
    const stop = () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
    const attempt = () => {
      frame = 0;
      if (stopped) return;
      const reachable = document.documentElement.scrollHeight - window.innerHeight;
      if (reachable >= target) {
        window.scrollTo(0, target);
        stop();
        return;
      }
      if (Date.now() >= deadline) {
        // As close as this page can get, rather than nothing.
        window.scrollTo(0, Math.max(0, reachable));
        stop();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    attempt();
    return stop;
  }, [location.key, location.pathname, location.search, navigationType]);
}
